import {  X, Plus, ChevronDown, Shuffle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { navigationItems } from '@/lib/constants'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import useUIStore from '@/store/useUIStore'
// import useAuthStore from '@/store/useAuthStore'

interface NavigationProps {
  mobileSidebarOpen: boolean
  setMobileSidebarOpen: (open: boolean) => void
  isExpanded: boolean
  setIsExpanded: (expanded: boolean) => void
}

export default function Navigation({
  mobileSidebarOpen,
  setMobileSidebarOpen,
  isExpanded,
  setIsExpanded
}: NavigationProps) {
  const location = useLocation()
  const navigate = useNavigate()
  // const { logout } = useAuthStore()
  const [navigationError, setNavigationError] = useState<string | null>(null)
  const navigationAttempts = useRef<{ [key: string]: number }>({})
  const MAX_ATTEMPTS = 3

  // Add state to track which category dropdowns are open
  const [openCategories, setOpenCategories] = useState<{
    explore: boolean;
    projects: boolean;
    friends: boolean;
    community: boolean;
    queue: boolean;
  }>({
    explore: false,
    projects: false,
    friends: false,
    community: false,
    queue: false,
  });

  // Get theme colors for logo color calculation
  const { backgroundSecondaryColor, uiColor, topNavColor } = useUIStore();

  // Calculate the best logo color based on contrast (same logic as text color)
  const logoColor = useMemo(() => {
    const calculateContrastRatio = (color1: string, color2: string): number => {
      const getLuminance = (color: string): number => {
        const hex = color.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16) / 255;
        const g = parseInt(hex.slice(2, 4), 16) / 255;
        const b = parseInt(hex.slice(4, 6), 16) / 255;

        const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        const lr = toLinear(r) * 0.2126;
        const lg = toLinear(g) * 0.7152;
        const lb = toLinear(b) * 0.0722;

        return lr + lg + lb;
      };

      const lum1 = getLuminance(color1);
      const lum2 = getLuminance(color2);
      const lighter = Math.max(lum1, lum2);
      const darker = Math.min(lum1, lum2);

      return (lighter + 0.05) / (darker + 0.05);
    };

    const white = '#ffffff';
    const black = '#000000';

    // Calculate contrast ratios against theme colors
    const bgWhiteContrast = calculateContrastRatio(backgroundSecondaryColor, white);
    const bgBlackContrast = calculateContrastRatio(backgroundSecondaryColor, black);
    const uiWhiteContrast = calculateContrastRatio(uiColor, white);
    const uiBlackContrast = calculateContrastRatio(uiColor, black);
    const navWhiteContrast = calculateContrastRatio(topNavColor, white);
    const navBlackContrast = calculateContrastRatio(topNavColor, black);

    // Average contrast across all theme colors
    const avgWhiteContrast = (bgWhiteContrast + uiWhiteContrast + navWhiteContrast) / 3;
    const avgBlackContrast = (bgBlackContrast + uiBlackContrast + navBlackContrast) / 3;

    // Return the color with better average contrast
    return avgWhiteContrast > avgBlackContrast ? white : black;
  }, [backgroundSecondaryColor, uiColor, topNavColor]);

  // Toggle function for category dropdowns
  const toggleCategory = (category: 'explore' | 'projects' | 'friends' | 'community' | 'queue') => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Reset all dropdowns when sidebar collapses so they exit smoothly
  // useEffect(() => {
  //   if (!isExpanded && !mobileSidebarOpen) {
  //     setOpenCategories({ explore: false, projects: false, friends: false, community: false, queue: false });
  //   }
  // }, [isExpanded, mobileSidebarOpen]);

  // Reset error state and navigation attempts on successful navigation
  useEffect(() => {
    setNavigationError(null)
    navigationAttempts.current = {}
  }, [location.pathname])

  // Handle navigation with retry logic and fallback
  const handleNavigation = useCallback((href: string) => {
    try {
      // Prevent navigation if already on the same route
      if (location.pathname === href) {
        setMobileSidebarOpen(false)
        return
      }

      // Reset attempt counter if navigating to a new route
      if (navigationAttempts.current[href] === undefined) {
        navigationAttempts.current = {}
      }

      // Check if we've exceeded max attempts for this route
      const attempts = navigationAttempts.current[href] || 0
      if (attempts >= MAX_ATTEMPTS) {
        console.error(`Failed to navigate to ${href} after ${MAX_ATTEMPTS} attempts`)
        setNavigationError(`Navigation failed. Redirecting to explore page...`)
        // Force navigate to explore page after delay
        setTimeout(() => {
          navigate('/explore', { replace: true })
        }, 1500)
        return
      }

      // Increment attempt counter
      navigationAttempts.current[href] = attempts + 1

      // Attempt navigation with replace if it's a retry
      navigate(href, attempts > 0 ? { replace: true } : undefined)
      setMobileSidebarOpen(false)

      // Clear navigation attempts on successful navigation
      setTimeout(() => {
        if (location.pathname === href) {
          navigationAttempts.current = {}
        }
      }, 500)
    } catch (error) {
      console.error('Navigation error:', error)
      setNavigationError('Failed to navigate. Please try again.')
    }
  }, [navigate, setMobileSidebarOpen, location.pathname])

  // Clear navigation error after delay
  useEffect(() => {
    if (navigationError) {
      const timer = setTimeout(() => {
        setNavigationError(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
    return; // Explicitly return undefined if no cleanup needed
  }, [navigationError])

  function NavItem({
    to,
    icon: Icon,
    label,
    mobile,
    subtext,
    subItems,
    categoryKey,
  }: {
    to: string
    icon: LucideIcon
    label: string
    mobile: boolean
    subtext?: string
    subItems?: { to: string; label: string; icon?: LucideIcon }[]
    categoryKey?: 'explore' | 'projects' | 'friends' | 'community' | 'queue'
  }) {
    const isActive = location.pathname === to || location.pathname.startsWith(to + '/')
    const hasSubItems = subItems && subItems.length > 0;
    const isDropdownOpen = categoryKey ? openCategories[categoryKey] : false;

    return (
      <div className="mb-1 min-h-[45.5px]">
        <div className="relative flex items-center min-h-[45.5px]">
          {/* Main link for navigation */}
          <Link
            to={to}
            className={cn(
              'flex items-center w-full py-2.5 px-4 rounded-lg min-w-0',
              isActive
                ? 'text-white'
                : 'text-zinc-400 hover:text-white',
              hasSubItems && (isExpanded || (mobile && mobileSidebarOpen)) && 'pr-10' // Add padding to make space for the button
            )}
            onClick={() => {
              // Only handle navigation, close mobile sidebar if needed
              if (!hasSubItems) {
                setMobileSidebarOpen(false);
              }
              handleNavigation(to);
            }}
          >
            <span className="inline-flex items-center justify-center w-5 h-5 shrink-0 grow-0 basis-auto flex-none">
              <Icon className="w-5 h-5 aspect-square shrink-0 grow-0 basis-auto flex-none" />
            </span>
            {(isExpanded || (mobile && mobileSidebarOpen)) && (
              <div
                className="ml-3 overflow-hidden whitespace-nowrap flex items-center"
                style={{
                  maxWidth: (isExpanded || (mobile && mobileSidebarOpen)) ? 200 : 0,
                  opacity: (isExpanded || (mobile && mobileSidebarOpen)) ? 1 : 0,
                  transition: 'max-width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1)'
                }}
              >
                <div className="mr-4">{label}</div>
                {subtext && <div className="text-xs text-zinc-500">{subtext}</div>}
              </div>
            )}
          </Link>

          {/* Dropdown toggle button (only shown when expanded and has subitems) */}
          {hasSubItems && (isExpanded || (mobile && mobileSidebarOpen)) && (
            <button
              onClick={(e) => {
                e.preventDefault(); // Prevent link navigation
                e.stopPropagation(); // Prevent triggering link's onClick
                toggleCategory(categoryKey!);
              }}
              className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-zinc-400 hover:text-white hover:bg-container-800",
                isDropdownOpen && "text-white bg-container-800"
              )}
              aria-label={isDropdownOpen ? `Collapse ${label}` : `Expand ${label}`}
              aria-expanded={isDropdownOpen}
            >
              <motion.div
                animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                <ChevronDown className="h-4 w-4" />
              </motion.div>
            </button>
          )}
        </div>

        {/* Animate dropdown sub-items */}
        {hasSubItems && (
          <AnimatePresence initial={false}>
            {isDropdownOpen && (isExpanded || (mobile && mobileSidebarOpen)) && (
              <motion.div
                key={`dropdown-${categoryKey}`}
                initial={{ scaleY: 0, opacity: 0 }}
                animate={{ scaleY: 1, opacity: 1 }}
                exit={{ scaleY: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                style={{ transformOrigin: 'top' }}
              >
                <div className="flex flex-col gap-2 mt-2 pl-4">
                  {subItems?.map((subItem, index) => {
                    const SubIcon = subItem.icon;
                    return (
                      <motion.div
                        key={subItem.to}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ delay: index * 0.03, duration: 0.2, ease: 'easeInOut' }}
                      >
                        <Link
                          to={subItem.to}
                          className={cn(
                            'flex items-center py-1.5 px-4 rounded-lg text-sm',
                            location.pathname === subItem.to
                              ? 'text-white bg-[rgba(var(--accent-1),0.1)]'
                              : 'text-zinc-400 hover:text-white'
                          )}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent default Link behavior to use our handler
                            handleNavigation(subItem.to);
                          }}
                        >
                          {SubIcon && (
                            <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 flex-none">
                              <SubIcon className="w-4 h-4 aspect-square shrink-0 flex-none" />
                            </span>
                          )}
                          <span className="ml-2">{subItem.label}</span>
                        </Link>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    )
  }

  return (
    <>
      {navigationError && (
        <div className="fixed top-4 right-4 bg-red-500/80 text-white px-4 py-2 rounded-lg z-50">
          {navigationError}
        </div>
      )}

      {/* Mobile sidebar */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs lg:hidden"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{
                type: 'spring',
                damping: 20,
                stiffness: 250,
                mass: 1
              }}
              className="fixed inset-y-0 left-0 z-50 w-72 backdrop-blur-xs border-r border-zinc-800 lg:hidden flex flex-col"
              style={{ backgroundColor: 'rgba(var(--sidebar-bg-color), var(--container-transparency))' }}
              data-liquid-reflect
            >
              <div className="flex h-16 items-center justify-between px-6">
                <Link to="/" className="flex items-center space-x-2">
                  {/* Syncreos Logo */}
                  <img
                    src="/logos/logo-icon.svg"
                    alt="Syncreos Logo"
                    className="h-6 w-6 text-white mr-1 object-contain"
                    style={{
                      filter: logoColor === '#000000' ? 'brightness(0) saturate(100%)' : 'brightness(0) invert(1)',
                      transform: `rotate(${isExpanded ? '0deg' : '40deg'})`,
                      transition: 'transform 300ms cubic-bezier(0.16,1,0.3,1)'
                    }}
                  />
                  <img
                    src="/logos/logo-font.svg"
                    alt="Syncreos Logo"
                    className="max-w-[150px] font-light tracking-wider text-white object-contain"
                    style={{ filter: logoColor === '#000000' ? 'brightness(0) saturate(100%)' : 'brightness(0) invert(1)' }}
                  />
                </Link>
                <button onClick={() => setMobileSidebarOpen(false)}>
                  <X className="h-6 w-6 text-zinc-400 hover:text-white" />
                </button>
              </div>
              <div className="px-3 py-2">
                <Link
                  to="/projects/new"
                  className="flex items-center space-x-3 rounded-lg px-4 py-2.5 text-sm font-light tracking-wider text-zinc-400 hover:text-white"
                >
                  <Plus className="shrink-0 w-5 h-5" />
                  <motion.span
                    initial={false}
                    animate={{ opacity: (isExpanded || mobileSidebarOpen) ? 1 : 0, width: (isExpanded || mobileSidebarOpen) ? 'auto' : 0 }}
                    className="ml-3 overflow-hidden whitespace-nowrap"
                  >
                    New Project
                  </motion.span>
                </Link>
              </div>
              <nav className="space-y-2 px-3 py-4">
                {navigationItems.map((item) => (
                  <NavItem
                    key={item.name}
                    to={item.href}
                    icon={item.icon}
                    label={item.name}
                    subtext={item.subtext}
                    subItems={item.subItems}
                    categoryKey={item.categoryKey}
                    mobile={true}
                  />
                ))}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <div className="hidden lg:block lg:fixed lg:inset-y-0 z-50">
        <motion.div
          className={cn(
            'relative h-full backdrop-blur-xs border-r border-zinc-800 transition-[width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-[width]',
            isExpanded ? 'w-64' : 'w-20'
          )}
          style={{ backgroundColor: 'rgba(var(--sidebar-bg-color), var(--container-transparency))' }}
          onMouseEnter={() => setIsExpanded(true)}
          onMouseLeave={() => setIsExpanded(false)}
          data-liquid-reflect
        >
          <div className="flex h-16 items-center px-6">
            <Link to="/" className="flex items-center">
              {/* Icon */}
                  <img
                    src="/logos/logo-icon.svg"
                    alt="Syncreos Logo Icon"
                    className="h-8 w-8 text-white shrink-0 flex-none object-contain block"
                    style={{
                      filter: logoColor === '#000000' ? 'brightness(0) saturate(100%)' : 'brightness(0) invert(1)',
                      transform: `rotate(${isExpanded ? '0deg' : '40deg'})`,
                      transition: 'transform 300ms cubic-bezier(0.16,1,0.3,1)'
                    }}
                  />
              {/* Animated container (CSS max-width + opacity, no transforms) */}
              <div
                className="ml-3 overflow-hidden"
                style={{
                  maxWidth: isExpanded ? 150 : 0,
                  opacity: isExpanded ? 1 : 0,
                  transition: 'max-width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1)'
                }}
              >
                <div className="w-[150px]">
                  <img
                    src="/logos/logo-font.svg"
                    alt="Syncreos Logo Text"
                    className="w-full h-auto"
                    style={{ filter: logoColor === '#000000' ? 'brightness(0) saturate(100%)' : 'brightness(0) invert(1)' }}
                  />
                </div>
              </div>
            </Link>
          </div>
          <div className="px-3 py-2 space-y-1">
            <Link
              to="/projects/new"
              className="flex items-center space-x-3 rounded-lg px-4 py-2.5 text-sm font-light tracking-wider text-zinc-400 hover:text-white"
            >
              <Plus className="shrink-0 w-5 h-5" />
              <span
                className="ml-3 overflow-hidden whitespace-nowrap"
                style={{
                  maxWidth: isExpanded ? 160 : 0,
                  opacity: isExpanded ? 1 : 0,
                  transition: 'max-width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1)'
                }}
              >
                New Project
              </span>
            </Link>
            <Link
              to="/remix"
              className="flex items-center space-x-3 rounded-lg px-4 py-2.5 text-sm font-light tracking-wider text-zinc-400 hover:text-white"
            >
              <Shuffle className="shrink-0 w-5 h-5" />
              <span
                className="ml-3 overflow-hidden whitespace-nowrap"
                style={{
                  maxWidth: isExpanded ? 160 : 0,
                  opacity: isExpanded ? 1 : 0,
                  transition: 'max-width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1)'
                }}
              >
                Remix
              </span>
            </Link>
          </div>

          {/* Horizontal divider */}
          <div className="px-3 py-2 pb-0">
            <div
              className="h-px"
              style={{
                backgroundColor: 'rgba(63, 63, 70, var(--container-transparency))',
              }}
            />
          </div>

          <nav className="flex-1 space-y-2 px-3 py-4">
            {navigationItems.map((item) => (
              <NavItem
                key={item.name}
                to={item.href}
                icon={item.icon}
                label={item.name}
                subtext={item.subtext}
                subItems={item.subItems}
                categoryKey={item.categoryKey}
                mobile={false}
              />
            ))}
          </nav>
        </motion.div>
      </div>
    </>
  )
} 